import type { ChangePasswordInput } from "@closer/shared";
import { changePasswordSchema } from "@closer/shared";
import { Button, Card, Checkbox, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * ŞİFRE (CLOS-392 `[S03]` — "Parola değiştirme ekranı ve oturum sonlandırma seçeneği").
 *
 * ⚠ Kart referansı 26 Ağustos'ta DÜZELTİLDİ: burada "CLOS-66 `[021]`" yazıyordu, oysa
 * CLOS-66 `[018]` 2FA kartı ve `[021]` başka bir işin sırası. Bu bölümün kartı CLOS-392.
 *
 * ### ⚠ Uç vardı, ekran yoktu
 *
 * `POST /api/profile/password` sunucuda hazırdı ama onu çağıran hiçbir yüzey yoktu:
 * kullanıcı şifresini üründen değiştiremiyordu (kullanıcı bildirdi, 26 Ağustos).
 *
 * ### ⚠ Parolası OLMAYAN hesap ayrı bir dünya
 *
 * Google ile açılan hesapta parola satırı hiç doğmuyor; "mevcut şifreniz" kutusuna
 * yazılacak bir şey yok. Ekran bu durumu tahmin etmiyor, sunucudan gelen `hasPassword`
 * ile ayırıyor ve o kullanıcıya değiştirme formu yerine ŞİFRE BELİRLEME yolunu sunuyor.
 * Bağlantı kendi e-posta adresine gidiyor: oturumu ele geçiren biri, posta kutusuna da
 * erişemedikçe parola belirleyemez.
 *
 * ### ⚠ Mevcut şifre HER ZAMAN isteniyor
 *
 * Yeniden doğrulama penceresi yok (sunucunun kuralı): açık bırakılmış bir tarayıcı
 * başındaki biri şifreyi değiştirememeli.
 */
type Profil = { profile: { hasPassword: boolean } };

export function PasswordSection() {
  const toast = useToast();

  const profil = useQuery({
    queryKey: ["profile", "sifre-durumu"],
    queryFn: async () => (await api<Profil>("/api/profile")).data,
  });

  const form = useZodForm(changePasswordSchema, {
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      code: "",
      revokeOtherSessions: true,
    } as ChangePasswordInput,
  });

  const degistir = useMutation({
    mutationFn: (girdi: ChangePasswordInput) => {
      /** ⚠ Boş kod GÖNDERİLMİYOR: şema trim'li ve isteğe bağlı, boş metin isteği takardı. */
      const { code, ...gerisi } = girdi;
      const govde = code && code.trim().length > 0 ? { ...gerisi, code: code.trim() } : gerisi;
      return api<{ revokedSessions: number }>("/api/profile/password", {
        method: "POST",
        body: JSON.stringify(govde),
      });
    },
    onSuccess: (yanit) => {
      form.reset({ currentPassword: "", newPassword: "", code: "", revokeOtherSessions: true });
      toast.success(
        yanit.data.revokedSessions > 0
          ? `Şifreniz değiştirildi; ${yanit.data.revokedSessions} oturum kapatıldı`
          : "Şifreniz değiştirildi",
      );
    },
    onError: (hata: unknown) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Şifre değiştirilemedi");
    },
  });

  const belirlemeBaglantisi = useMutation({
    mutationFn: () =>
      api<{ sent: boolean }>("/api/profile/password/setup-link", {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => toast.success("Şifre belirleme bağlantısı e-postanıza gönderildi"),
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Bağlantı gönderilemedi"),
  });

  const gonder = form.handleSubmit((girdi) => degistir.mutate(girdi));

  return (
    <Card title="Şifre">
      {profil.isPending ? (
        <div role="status" aria-label="Şifre durumu yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : profil.error ? (
        /*
          ⚠ SORGU DÜŞERSE KART DA KAPANIR (CLOS-806 denetimi).

          Bu dalın yokluğu sessiz bir yanlış anlatımdı: `hasPassword` gelmediğinde
          `profil.data?.profile.hasPassword === false` da `false` oluyor ve ekran son dala,
          yani ŞİFRE DEĞİŞTİRME formuna düşüyordu. Parolası hiç olmayan bir kullanıcıya
          "Mevcut şifreniz" soruluyordu: yazacak bir şeyi yok, yazdığı her şey reddediliyor
          ve ekran ona hesabında şifre VARMIŞ gibi davranıyor — kartın *"hesap ekranı
          kullanıcının durumunu doğru anlatır"* kriterinin tam tersi.

          Kusur sınıfı depoda kayıtlı: kapıyı besleyen sorgu düşerse kapı KAPANMALI; arıza
          "durum böyle" gibi görünmemeli.
        */
        <StateView
          state={resolveErrorState(profil.error, {
            idempotent: true,
            overrides: {
              error: {
                title: "Şifre durumu okunamadı",
                description:
                  "Hesabınızda şifre tanımlı olup olmadığı şu an getirilemedi; tekrar deneyebilirsiniz.",
              },
            },
          })}
          onRetry={() => void profil.refetch()}
        />
      ) : profil.data?.profile.hasPassword === false ? (
        <div className="flex flex-col gap-3">
          {/* ⚠ Parolasız hesaba "mevcut şifreniz" sorulmuyor: yazacağı bir şey yok. */}
          <p className="text-sm text-fg">
            Bu hesap şifre olmadan açılmış (ör. Google ile giriş). Şifreyle de girebilmek isterseniz
            e-posta adresinize bir belirleme bağlantısı gönderelim; Google ile giriş yolunuz açık
            kalır.
          </p>
          <div className="flex">
            <Button
              type="button"
              loading={belirlemeBaglantisi.isPending}
              onClick={() => belirlemeBaglantisi.mutate()}
            >
              Şifre belirleme bağlantısı gönder
            </Button>
          </div>
        </div>
      ) : (
        <form
          /* ⚠ Formun adı var: sayfadaki diğer "Mevcut şifreniz" alanından ayıran tek şey. */
          aria-label="Şifreyi değiştir"
          noValidate
          className="flex flex-col gap-4"
          onSubmit={gonder}
        >
          <FormField
            label="Mevcut şifreniz"
            error={form.formState.errors.currentPassword?.message}
            required
          >
            <Input
              type="password"
              autoComplete="current-password"
              {...form.register("currentPassword")}
            />
          </FormField>

          <FormField
            label="Yeni şifreniz"
            error={form.formState.errors.newPassword?.message}
            required
          >
            <Input type="password" autoComplete="new-password" {...form.register("newPassword")} />
          </FormField>

          {/*
            ⚠ İKİNCİ FAKTÖR ALANI (BUG-022 · Mega E2E'de ölçüldü).

            2FA'sını açan kullanıcı şifresini üründen bir daha DEĞİŞTİREMİYORDU: sunucu
            `second_factor_required` ile 403 veriyor, şema `code` alanını taşıyor, ama
            formda o alan yoktu. Yani güvenliğini artıran kullanıcı, hesabını yönetme
            yeteneğini kaybediyordu.

            ⚠ Alan İSTEĞE BAĞLI ve boşken gövdeye GİRMİYOR — `AccountClosureSection`
            emsalinin aynısı. Boş metin göndermek 2FA'sı olmayan herkesin isteğini
            şemaya takardı; yani alanı eklemek, onu kullanmayanları bozardı.
          */}
          <FormField
            label="Doğrulama kodu (2FA açıksa)"
            error={form.formState.errors.code?.message}
          >
            <Input autoComplete="one-time-code" inputMode="numeric" {...form.register("code")} />
          </FormField>

          {/*
            ⚠ Varsayılan AÇIK: şifre değiştirmenin en yaygın sebebi hesabın başkasının
            eline geçmiş olması; diğer oturumlar ayakta kalırsa yeni şifre işe yaramaz.
          */}
          <Checkbox
            label="Diğer cihazlardaki oturumları kapat"
            {...form.register("revokeOtherSessions")}
          />

          <div className="flex">
            <Button type="submit" loading={degistir.isPending}>
              Şifreyi değiştir
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
